import React from 'react';
import {
  Drawer,
  Typography,
  IconButton,
  Stack,
  Divider,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';
import PaiButton from 'features/ui/base-components/pai-button';

interface Props {
  open: boolean;
  funnel: any | null;
  likeLoading: boolean;
  isMostPopular: boolean;
  isBestConversion: boolean;
  onClose(): void;
  onLike(funnelId: string): void;
  onApply(funnel: any): void;
}

/** Same badge styling as the card, so a template looks the same in the grid and in the drawer. */
const badgeSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  borderRadius: '6px',
  px: '8px',
  py: '4px',
} as const;

const FunnelDetailsDrawer: React.FC<Props> = ({ open, funnel, likeLoading, isMostPopular, isBestConversion, onClose, onLike, onApply }) => {
  const { t, i18n } = useTranslation();

  const conversion = Number(funnel?.conversionRate);
  const conversionLabel = Number.isFinite(conversion)
    ? `${conversion.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
    : '–';
  const usesLabel = String(Number(funnel?.useCount ?? funnel?.uses ?? 0));
  const stepsLabel = String(Array.isArray(funnel?.steps) ? funnel.steps.length : Number(funnel?.steps ?? 0));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 480, p: '28px 32px' } }}>
      {funnel && (
        <Stack gap="14px" sx={{ height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Typography sx={{ fontSize: '26px', fontWeight: 500, letterSpacing: '-0.2px', lineHeight: 1.3 }}>
              {funnel.name}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {(isMostPopular || isBestConversion) && (
            <Stack direction="row" gap="6px" flexWrap="wrap">
              {isMostPopular && (
                <Box
                  component="span"
                  sx={{ ...badgeSx, backgroundColor: (theme) => theme.palette.backgrounds.lightOrange, color: '#8A3F01' }}
                >
                  {t('useCaseStorePopular')}
                </Box>
              )}
              {isBestConversion && (
                <Box
                  component="span"
                  sx={{ ...badgeSx, backgroundColor: (theme) => theme.palette.backgrounds.lightGreen, color: '#2C5A00' }}
                >
                  {t('useCaseStoreTopConversion')}
                </Box>
              )}
            </Stack>
          )}

          <Divider />

          <Typography sx={{ fontSize: '14px', color: '#555', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
            {funnel.description}
          </Typography>

          {/* The same three metrics the card shows, so the drawer confirms rather than reframes. */}
          <Stack
            direction="row"
            gap="12px"
            sx={{ py: '12px', borderTop: '1px solid #F0F0F5', borderBottom: '1px solid #F0F0F5' }}
          >
            {[
              { label: t('useCaseStoreMetricConversion'), value: conversionLabel },
              { label: t('useCaseStoreMetricUses'), value: usesLabel },
              { label: t('useCaseStoreMetricSteps'), value: stepsLabel },
            ].map((metric) => (
              <Stack key={metric.label} sx={{ flex: 1, gap: '2px' }}>
                <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.greys.medium }}>
                  {metric.label}
                </Typography>
                <Typography sx={{ fontSize: '17px', fontWeight: 500 }}>{metric.value}</Typography>
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" alignItems="center" gap="12px">
            <Box
              component="span"
              sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: (theme) => theme.palette.greys.medium }}
            >
              <IconButton size="small" sx={{ p: 0 }} onClick={() => onLike(funnel._id)} disabled={likeLoading}>
                <StarIcon sx={{ fontSize: '18px' }} color={likeLoading ? 'disabled' : funnel.liked ? 'warning' : 'action'} />
              </IconButton>
              {Number(funnel.likes ?? funnel.likeCount ?? 0)}
            </Box>
            {funnel.creatorName && (
              <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.greys.medium }}>
                {funnel.creatorName}
              </Typography>
            )}
            <Box sx={{ ml: 'auto' }}>
              <PaiButton
                text={t('useCaseStoreApply')}
                border="round"
                size="small"
                action={() => onApply(funnel)}
                style={{ height: 36, padding: '0 18px', fontSize: 14, fontWeight: 700, borderRadius: 18 }}
              />
            </Box>
          </Stack>

          <Box flex={1} />
        </Stack>
      )}
    </Drawer>
  );
};

export default FunnelDetailsDrawer;